import { FC, useContext, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import {
  OnlineDepositsIcon,
  Separator,
  Tooltip,
  WalletIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { NavLink } from 'react-router-dom';

import { AuthContext } from 'auth';
import { FeatureHOC, useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';
import { isSandbox } from 'sandbox';
import { SettingsContext } from 'settings';
import { formatSum } from 'utils';

import {
  IMovementMoneyProps,
  IGetMovementMoneyDataResponse,
  IGetMovementMoneyDataRequest,
} from './interfaces';
import s from './MovementMoney.module.scss';
import { GET_MOVEMENT_MONEY_DATA } from './schema';

export const MovementMoney: FC<IMovementMoneyProps> = ({
  workShiftId,
  small,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: {
        currency,
        tinkoffSbpEnabled,
        kaspiOnlineEnabled,
        cloudPayments,
        countryCode,
      },
    },
  } = useContext(SettingsContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const isSbp_tinkoff = useIsFeatureAvailable('sbp_tinkoff');

  const { data: workshiftMoneyData } = useQuery<
    IGetMovementMoneyDataResponse,
    IGetMovementMoneyDataRequest
  >(GET_MOVEMENT_MONEY_DATA, {
    fetchPolicy: 'cache-only',
    skip: !permissions?.has('work_shift_active'),
    variables: { id: workShiftId },
  });

  const { deposit, online_deposit } =
    workshiftMoneyData?.getWorkShiftPaymentOverviewData ?? {};

  const isOnlinePaymentEnabled = useMemo<boolean>(() => {
    if (countryCode === 'RU') return tinkoffSbpEnabled || cloudPayments;
    if (countryCode === 'KZ') return kaspiOnlineEnabled || cloudPayments;

    return cloudPayments;
  }, [countryCode, kaspiOnlineEnabled, cloudPayments, tinkoffSbpEnabled]);

  return (
    <div className={cn(s.money, small && s.small, !isSbp_tinkoff && s.split)}>
      <div className={cn(s.block, s.deposit)}>
        <span className={s.title}>
          <WalletIcon size="m" color="violet" />
          {t('category.deposits.spend')}
        </span>
        <span>
          {formatSum(deposit, currency.sign, { maximumFractionDigits: 2 })}
        </span>
      </div>
      {isSbp_tinkoff && <Separator className={s.separator} />}
      <FeatureHOC
        alias="sbp_tinkoff"
        strategy="violetReplace"
        hideTitle
        variant="secondary"
      >
        <div className={cn(s.block, s.sbp)}>
          <span className={s.title}>
            <OnlineDepositsIcon
              size="m"
              className={s.sbp_icon}
              color={'violet'}
            />
            <span className={s.title_text}>{t('category.online_deposit')}</span>
            {!isSandbox && !isOnlinePaymentEnabled && (
              <Tooltip
                className={s.tooltip}
                contentClassName={s.tooltip_content}
                content={
                  <div className={s.tooltip_block}>
                    <span className={s.tooltip_text}>
                      {t('dashboard.online_payment.tooltip.message')}
                    </span>
                    <NavLink
                      state={{ selectedTab: 'CASH_BOX' }}
                      to={'/settings'}
                      className={s.tooltip_footer}
                    >
                      {t('dashboard.online_payment.tooltip.button')}
                    </NavLink>
                  </div>
                }
              />
            )}
          </span>
          <span>
            {formatSum(online_deposit, currency.sign, {
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      </FeatureHOC>
    </div>
  );
};
